import React from 'react';
import { sectionStyles, getAnimation, hexToRgba } from '../utils';
import useScrollReveal from '../useScrollReveal';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import SectionBgImage from './SectionBgImage';


export default function ChatPreviewSection({ block = {}, primaryColor, secondaryColor, enableAnimations, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'simple';
    const messages = content.items || [];
    const showTyping = config.show_typing !== false;
    const showTimestamps = config.show_timestamps !== false;
    const botName = content.bot_name || 'Asistente';
    const botAvatar = content.bot_avatar || '';
    const accentColor = primaryColor;

    if (messages.length === 0) return null;

    const MessageBubble = ({ msg, idx }) => {
        const isBot = msg.sender === 'bot';
        const delayMs = enableAnimations ? idx * 150 : 0;
        return (
            <div className={`flex ${isBot ? 'justify-start' : 'justify-end'} ${anim}`}
                style={{ animationDelay: `${delayMs}ms` }}>
                <div className={`max-w-[80%] ${isBot ? 'order-2' : ''}`}>
                    {isBot && botName && <p className={`text-[10px] font-medium mb-0.5 ${s.dark ? 'text-white/50' : 'text-gray-400'}`}>{botName}</p>}
                    <div className={`px-4 py-2.5 text-sm leading-relaxed ${isBot
                        ? `rounded-2xl rounded-tl-md ${s.dark ? 'bg-white/10 text-white' : 'text-gray-800'}`
                        : `rounded-2xl rounded-tr-md text-white`
                    }`} style={isBot
                        ? (s.dark ? {} : { backgroundColor: hexToRgba(accentColor, 0.08) })
                        : { backgroundColor: accentColor }
                    }>
                        {msg.text}
                        {msg.badge && <span className="ml-2 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-white/20">{msg.badge}</span>}
                    </div>
                    {showTimestamps && msg.timestamp && (
                        <p className={`text-[9px] mt-0.5 ${isBot ? 'text-left' : 'text-right'} ${s.dark ? 'text-white/30' : 'text-gray-300'}`}>
                            {msg.timestamp}
                            {!isBot && <span className="ml-1" style={{ color: accentColor }}>✓✓</span>}
                        </p>
                    )}
                </div>
                {isBot && botAvatar && <img src={botAvatar} alt="" className="w-8 h-8 rounded-full object-cover shrink-0 order-1 mr-2 mt-auto" />}
            </div>
        );
    };

    const TypingIndicator = () => {
        if (!showTyping) return null;
        return (
            <div className="flex justify-start">
                {botAvatar && <div className="w-8 h-8 mr-2" />}
                <div className={`px-4 py-3 rounded-2xl rounded-tl-md ${s.dark ? 'bg-white/10' : 'bg-gray-100'}`}>
                    <div className="flex gap-1">
                        {[0, 1, 2].map(i => (
                            <div key={i} className={`w-2 h-2 rounded-full ${s.dark ? 'bg-white/40' : 'bg-gray-400'}`}
                                style={{ animation: 'pulse 1.4s ease-in-out infinite', animationDelay: `${i * 200}ms` }} />
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    // Variants
    if (variant === 'split') {
        return (
            <section className={`${s.padClass} relative overflow-hidden ${s.radiusClass}`} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} position="top" color={s.accent} />

                <div ref={ref} className={`relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <div className="grid md:grid-cols-2 gap-8 lg:gap-16 items-center">
                        <div>
                            <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                            {content.body && <p className={`mt-4 text-base ${s.dark ? 'text-white/70' : 'text-gray-600'}`}>{content.body}</p>}
                        </div>
                        <div className={`rounded-3xl p-5 sm:p-6 space-y-4 shadow-xl ${s.dark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-100'}`}>
                            {messages.map((msg, i) => <MessageBubble key={i} msg={msg} idx={i} />)}
                            <TypingIndicator />
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} position="bottom" color={s.accent} />
            </section>
        );
    }

    if (variant === 'phone') {
        return (
            <section className={`${s.padClass} relative overflow-hidden ${s.radiusClass}`} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} position="top" color={s.accent} />

                <div ref={ref} className={`relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="mt-8 flex justify-center">
                        <div className="w-full max-w-sm">
                            <div className="rounded-[2rem] border-4 border-gray-800 bg-gray-900 shadow-2xl overflow-hidden">
                                <div className="flex justify-center pt-2 pb-1 bg-gray-900"><div className="w-20 h-4 bg-gray-800 rounded-full" /></div>
                                <div className={`p-4 space-y-3 ${s.dark ? 'bg-gray-950' : 'bg-gray-50'}`} style={{ minHeight: 350 }}>
                                    <div className={`flex items-center gap-2 pb-3 border-b ${s.dark ? 'border-white/10' : 'border-gray-200'}`}>
                                        {botAvatar ? <img src={botAvatar} alt="" className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 rounded-full" style={{ backgroundColor: hexToRgba(accentColor, 0.2) }} />}
                                        <span className={`text-sm font-semibold ${s.dark ? 'text-white' : 'text-gray-900'}`}>{botName}</span>
                                        <span className="w-2 h-2 rounded-full bg-green-400 ml-auto" />
                                    </div>
                                    {messages.map((msg, i) => <MessageBubble key={i} msg={msg} idx={i} />)}
                                    <TypingIndicator />
                                </div>
                                <div className="flex justify-center py-2 bg-gray-900"><div className="w-24 h-1 bg-gray-700 rounded-full" /></div>
                            </div>
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} position="bottom" color={s.accent} />
            </section>
        );
    }

    // Default: simple centered chat
    return (
        <section className={`${s.padClass} relative overflow-hidden ${s.radiusClass}`} style={s.bgStyle}>
            <SectionDivider type={config.divider_top} position="top" color={s.accent} />
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
            <div ref={ref} className={`relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                <div className={`mt-8 rounded-3xl p-5 sm:p-6 space-y-4 shadow-xl ${s.dark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-100'}`}>
                    {messages.map((msg, i) => <MessageBubble key={i} msg={msg} idx={i} />)}
                    <TypingIndicator />
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} position="bottom" color={s.accent} />
        </section>
    );
}
